import { env_backend_url } from 'env_Variables';
import { toaster, toastTypeArray } from 'helpers/toastFactory';

import { t } from 'i18next';
import { ServerMessageResponse } from 'types';

function buildUrl(endPoint: string, queryParam?: string) {
  let url = `${env_backend_url}${endPoint}`;
  if (queryParam) {
    url += `?${queryParam}`;
  }
  return url;
}

export async function AuthPost(endPoint: string, body: object, token: string) {
  try {
    const response = await fetch(`${env_backend_url}${endPoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok) {
      handleErrorResponse(response.status, data);
      return;
    }
    const { type, message } = data;
    if (toastTypeArray.includes(type)) {
      toaster(type, t(message, { ns: 'serverMessages' }));
      return true;
    }
  } catch (error) {
    console.error(error);
    toaster('error', 'Something went Wrong!');
  }
}

export async function AuthUpload(
  endPoint: string,
  body: BodyInit,
  token: string
) {
  try {
    const response = await fetch(`${env_backend_url}${endPoint}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: body,
    });

    const data = await response.json();
    //console.log(data);
    if (!response.ok) {
      handleErrorResponse(response.status, data);
      return;
    }
    const { type, message } = data;
    if (toastTypeArray.includes(type)) {
      toaster(type, t(message, { ns: 'serverMessages' }));
      return type;
    }
  } catch (error) {
    console.error(error);
    toaster('error', 'Something went Wrong!');
  }
}

export async function AuthPut(
  endPoint: string,
  body: object,
  token: string,
  queryParam?: string
) {
  const url = buildUrl(endPoint, queryParam);
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();
  if (!response.ok) {
    handleErrorResponse(response.status, data);
    return;
  }

  const { type, message } = data;
  if (toastTypeArray.includes(type)) {
    return toaster(type, t(message, { ns: 'serverMessages' }));
  }
}

export async function AuthDel(endPoint: string, body: object, token: string) {
  try {
    const response = await fetch(`${env_backend_url}${endPoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    const data = await response.json();

    if (!response.ok) {
      handleErrorResponse(response.status, data);
      return;
    }
    const { type, message } = data;
    if (toastTypeArray.includes(type)) {
      return toaster(type, t(message, { ns: 'serverMessages' }));
    }
  } catch (error) {
    console.error(error);

    toaster('error', 'Something went Wrong!');
  }
}
export async function AuthPatch(endPoint: string, body: object, token: string) {
  try {
    const response = await fetch(`${env_backend_url}${endPoint}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    const data = await response.json();

    if (!response.ok) {
      handleErrorResponse(response.status, data);
      return;
    }
    const { type, message } = data;
    if (toastTypeArray.includes(type)) {
      return toaster(type, t(message, { ns: 'serverMessages' }));
    }
  } catch (error) {
    console.error(error);

    toaster('error', 'Something went Wrong!');
  }
}
export async function AuthGet(endPoint: string, token: string) {
  try {
    const response = await fetch(`${env_backend_url}${endPoint}`, {
      method: 'Get',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      const data = await response.json();
      handleErrorResponse(response.status, data);
      return;
    }

    return await response.json();
  } catch (error) {
    //console.log(error);
  }
}
// Progress reported by AuthDownload while the file transfers. `total` is null
// when the server didn't send a Content-Length, in which case `percent` is null
// and the caller should show an indeterminate state.
export interface DownloadProgress {
  loaded: number;
  total: number | null;
  percent: number | null;
}

// Authenticated binary download: fetches a file/zip with the bearer token and
// triggers a browser save. Used for the SFTP-backed downloads, which now sit
// behind JWT auth so a plain <a href> (no Authorization header) won't work.
//
// The body is streamed so the caller can render download progress via
// `onProgress`; a real percentage is reported once Content-Length is known,
// otherwise `percent` stays null (indeterminate). Returns true on success so
// the caller can distinguish completion from a handled error.
export async function AuthDownload(
  endPoint: string,
  token: string,
  fallbackName = 'download',
  onProgress?: (progress: DownloadProgress) => void
): Promise<boolean> {
  try {
    const response = await fetch(`${env_backend_url}${endPoint}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      let data: ServerMessageResponse | null = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }
      handleErrorResponse(response.status, data);
      return false;
    }

    // Prefer the server-provided filename (Content-Disposition) over the fallback.
    // filename*= (RFC 5987) is percent-encoded and must be decoded; the plain
    // filename= form is used verbatim (decoding it would corrupt names that
    // legitimately contain a literal '%').
    const disposition = response.headers.get('Content-Disposition') ?? '';
    let fileName = fallbackName;
    const starMatch = disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
    const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
    if (starMatch) {
      try {
        fileName = decodeURIComponent(starMatch[1].trim());
      } catch {
        fileName = starMatch[1].trim();
      }
    } else if (plainMatch) {
      fileName = plainMatch[1].trim();
    }

    const lengthHeader = response.headers.get('Content-Length');
    const total = lengthHeader ? parseInt(lengthHeader, 10) : NaN;
    const hasTotal = Number.isFinite(total) && total > 0;

    let blob: Blob;
    // Stream the body to report progress. Fall back to response.blob() if the
    // browser doesn't expose a readable body stream.
    if (response.body && typeof response.body.getReader === 'function') {
      const reader = response.body.getReader();
      const chunks: Uint8Array[] = [];
      let loaded = 0;

      onProgress?.({
        loaded: 0,
        total: hasTotal ? total : null,
        percent: hasTotal ? 0 : null,
      });

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) {
          chunks.push(value);
          loaded += value.length;
          onProgress?.({
            loaded,
            total: hasTotal ? total : null,
            percent: hasTotal ? Math.round((loaded / total) * 100) : null,
          });
        }
      }

      blob = new Blob(chunks as BlobPart[], {
        type:
          response.headers.get('Content-Type') ?? 'application/octet-stream',
      });
    } else {
      onProgress?.({ loaded: 0, total: null, percent: null });
      blob = await response.blob();
    }

    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
    return true;
  } catch (error) {
    console.error(error);
    toaster('error', 'Something went Wrong!');
    return false;
  }
}

export async function Post<T>(endPoint: string, body: T) {
  try {
    const response = await fetch(`${env_backend_url}${endPoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok) {
      handleErrorResponse(response.status, data);
      return;
    }
    const { type, message } = data;
    if (toastTypeArray.includes(type)) {
      toaster(type, t(message, { ns: 'serverMessages' }));
      return true;
    }
  } catch (error) {
    console.error(error);
    toaster('error', 'Something went Wrong!');
  }
}
function handleErrorResponse(
  status: number,
  data: ServerMessageResponse | null
) {
  const message = data?.message;

  // @ts-ignore
  if (message) return toaster('error', t(message, { ns: 'serverMessages' }));
  switch (status) {
    case 400:
      toaster('error', 'Bad Request');
      break;
    case 401:
      toaster('error', 'Unauthorized');
      break;
    case 403:
      toaster('error', 'Forbidden');
      break;
    case 404:
      toaster('error', 'Not Found');
      break;
    case 500:
      toaster('error', 'Internal Server Error');
      break;
    default:
      toaster('error', `Error (${status})`);
      break;
  }
}
